import { useCallback, useEffect, useState } from 'react';

import {
  ActionEntityType,
  ActionStatus,
  ChangeRequestResponse,
  changeRequestGetLatestChangeRequests,
  intermediaryGetResult,
  lenderFirmsGetResult,
  procurationFeesGetResult,
  productChangesGetResult,
} from '@pmp/api';

import { ChangeLogCollectionChange, ChangeLogEntry, ChangeLogFieldChange, ChangeLogTarget, ChangeOrigin } from './interfaces';
import { EXCLUDED_FIELDS } from './fields';
import { getSubTables, SubTable, SubTableRow } from './subTables';

// DEMO SCAFFOLD for PMP-2012.
//
// Nothing records per-entity change history yet; that is PMP-2009 (capture in broker-service) and
// PMP-2010 (the read endpoints). Until those land there is no `GET /intermediary/{id}/history` to
// call, so this hook assembles the drawer's rows from what the BFF can already answer.
//
// Real, from calls that already exist:
//   - the applied change requests for the entity TYPE, with their actionId, author and timestamp
//     (`/changeRequests/list`)
//   - each change request's own rows and the values they replaced (`/{entity}/{actionId}/result`,
//     which answers with `rows` and `originalRows` side by side). The record's row is found by id,
//     so a change request that never touched this record contributes nothing, and every field whose
//     value moved becomes an entry carrying both of its real values.
//
// Nothing here is invented: a record with no applied change request has an empty log, which is the
// truth about what PMP can answer today. What this cannot show is a change made outside a change
// request, the Broker Portal above all, because in PMP today the change request is the only trace a
// change leaves. That is the gap PMP-2009 closes.

/** How many applied change requests to look inside. Keeps the demo's fan-out bounded. */
const MAX_CHANGE_REQUESTS = 15;

/** How many pages of one change request to search for the record. A bulk import can be far longer. */
const MAX_PAGES = 5;

/** Enough rows to fill the drawer, so a busy entity does not turn the demo into a wall of changes. */
const MAX_ENTRIES = 10;

const APPLIED_STATUSES = new Set<string>([ActionStatus.APPLIED, ActionStatus.PARTIALLY_APPLIED]);

type ChangeRow = Record<string, unknown> & { rowNumber?: number };

type ChangeResult = {
  rows: ChangeRow[];
  originalRows: ChangeRow[];
  pageOptions?: { paginationToken?: string | null };
};

/**
 * The result endpoint of each entity the log covers. Every entity in the change request pipeline has
 * one, so an entity is added here and in `ID_FIELDS`, and the rest of the file already handles it.
 */
const RESULT_CALLS: Record<ChangeLogTarget['entityType'], (actionId: string, paginationToken?: string) => Promise<ChangeResult>> = {
  [ActionEntityType.BROKERS]: async (actionId, paginationToken) =>
    (await intermediaryGetResult({ path: { actionId }, query: { paginationToken } })).data as unknown as ChangeResult,
  [ActionEntityType.LENDER_FIRMS]: async (actionId, paginationToken) =>
    (await lenderFirmsGetResult({ path: { actionId }, query: { paginationToken } })).data as unknown as ChangeResult,
  [ActionEntityType.PRODUCTS]: async (actionId, paginationToken) =>
    (await productChangesGetResult({ path: { actionId }, query: { paginationToken } })).data as unknown as ChangeResult,
  [ActionEntityType.PROCURATION_FEES]: async (actionId, paginationToken) =>
    (await procurationFeesGetResult({ path: { actionId }, query: { paginationToken } })).data as unknown as ChangeResult,
};

/** What a change request row calls the record it changes, which is not `id` for every entity. */
const ID_FIELDS: Record<ChangeLogTarget['entityType'], string> = {
  [ActionEntityType.BROKERS]: 'id',
  [ActionEntityType.LENDER_FIRMS]: 'id',
  [ActionEntityType.PRODUCTS]: 'code',
  [ActionEntityType.PROCURATION_FEES]: 'id',
};

const originTag = (origin: ChangeOrigin) => ({ key: origin, value: origin });

const toEntry = (
  id: string,
  changedBy: string,
  changedAt: string | null,
  origin: ChangeOrigin,
  changeRequestId: string | null,
  changes: ChangeLogFieldChange[],
  collections: ChangeLogCollectionChange[],
): ChangeLogEntry => ({
  id,
  changedBy,
  changedAt,
  origin: originTag(origin),
  changeRequestId,
  // A collection's own fields count here too, once each however many of its rows moved: the filter has
  // to reach "the town of a trading address", which is a question the collection's name cannot answer.
  fields: [...new Set([...changes, ...collections.flatMap(collection => collection.changes)].map(change => change.field))],
  changes,
  collections,
});

/**
 * One cell as the log shows it, or null when the field held nothing. Collaborations and value lists
 * arrive as objects, so the readable label is preferred over the identifier it travels with.
 */
const formatValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;

  if (Array.isArray(value)) {
    const items = value.map(formatValue).filter(Boolean);

    return items.length ? items.join(', ') : null;
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const label = record.displayName ?? record.name ?? record.value ?? record.key ?? record.id;

    return label === undefined ? null : formatValue(label);
  }

  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  return null;
};

/** Real. The record's own row in one change request, paired with the values it replaced. */
const getRecordChange = async (
  target: ChangeLogTarget,
  actionId: string,
  signal: AbortSignal,
): Promise<{ row: ChangeRow; original: ChangeRow | undefined } | null> => {
  let paginationToken: string | undefined;

  for (let page = 0; page < MAX_PAGES; page += 1) {
    const data = await RESULT_CALLS[target.entityType](actionId, paginationToken);
    if (signal.aborted) return null;

    const index = data.rows.findIndex(row => row[ID_FIELDS[target.entityType]] === target.id);
    if (index >= 0) {
      const row = data.rows[index];
      const original = data.originalRows.find(candidate => candidate.rowNumber === row.rowNumber) ?? data.originalRows[index];

      return { row, original };
    }

    paginationToken = data.pageOptions?.paginationToken ?? undefined;
    if (!paginationToken) return null;
  }

  return null;
};

const indexByKey = (rows: SubTableRow[], rowKey: string): Record<string, SubTableRow> =>
  Object.fromEntries(rows.map((row, index) => [formatValue(row[rowKey]) ?? String(index), row]));

/**
 * Real. Every field of every row of one collection whose value the change request moved. Compared row
 * by row and then field by field, so a post code edited inside a trading address is a change to that
 * address's post code, and a row that arrived is every one of its fields moving off Empty.
 */
const toCollection = (
  field: string,
  subTable: SubTable,
  row: ChangeRow,
  original: ChangeRow | undefined,
): ChangeLogCollectionChange | null => {
  const hidden = new Set<string>(subTable.hiddenColumns ?? []);
  const columns = subTable.columns.map(column => String(column.key)).filter(key => !hidden.has(key));
  const current = indexByKey(subTable.buildRows(row), subTable.rowKey);
  const before = indexByKey(original ? subTable.buildRows(original) : [], subTable.rowKey);

  const changes = [...new Set([...Object.keys(before), ...Object.keys(current)])].flatMap(key => {
    const now = current[key];
    const then = before[key];
    const item = formatValue((now ?? then)?.[subTable.rowLabel]) ?? key;

    return columns
      .map(column => ({
        id: `${field}-${key}-${column}`,
        item,
        field: `${field}.${column}`,
        previousValue: formatValue(then?.[column]),
        newValue: formatValue(now?.[column]),
      }))
      .filter(change => change.previousValue !== change.newValue);
  });

  return changes.length ? { field, changes } : null;
};

const toCollections = (target: ChangeLogTarget, row: ChangeRow, original: ChangeRow | undefined): ChangeLogCollectionChange[] =>
  Object.entries(getSubTables(target.entityType))
    .map(([field, subTable]) => toCollection(field, subTable, row, original))
    .filter((collection): collection is ChangeLogCollectionChange => collection !== null);

/** Real. Every field of the record itself whose value the change request moved. */
const toChanges = (target: ChangeLogTarget, actionId: string, row: ChangeRow, original: ChangeRow | undefined): ChangeLogFieldChange[] => {
  const subTables = getSubTables(target.entityType);

  return Object.keys(row)
    .filter(field => field !== 'rowNumber' && field !== ID_FIELDS[target.entityType] && !EXCLUDED_FIELDS.has(field) && !subTables[field])
    .map(field => ({
      id: `${actionId}-${field}`,
      field,
      previousValue: formatValue(original?.[field]),
      newValue: formatValue(row[field]),
    }))
    .filter(change => change.previousValue !== change.newValue);
};

/** One log entry per change request that touched this record. Null when it did not. */
const toPortalEntry = async (
  target: ChangeLogTarget,
  changeRequest: ChangeRequestResponse,
  signal: AbortSignal,
): Promise<ChangeLogEntry | null> => {
  const actionId = changeRequest.actionId;
  if (!actionId) return null;

  try {
    const found = await getRecordChange(target, actionId, signal);
    if (!found) return null;

    const changes = toChanges(target, actionId, found.row, found.original);
    const collections = toCollections(target, found.row, found.original);
    if (!changes.length && !collections.length) return null;

    return toEntry(
      actionId,
      changeRequest.createdBy ?? '',
      changeRequest.creationTime ?? null,
      ChangeOrigin.PORTAL,
      actionId,
      changes,
      collections,
    );
  } catch {
    // A change request whose result cannot be read simply contributes no rows to the demo.
    return null;
  }
};

export const useChangeLogEntries = (target: ChangeLogTarget | null) => {
  const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  const load = useCallback(
    async (signal: AbortSignal) => {
      if (!target) return;
      setIsFetching(true);

      try {
        const { data } = await changeRequestGetLatestChangeRequests({ query: { entityType: target.entityType, limit: 50 } });
        if (signal.aborted) return;

        const applied = (data ?? []).filter(cr => APPLIED_STATUSES.has(cr.status?.key ?? '')).slice(0, MAX_CHANGE_REQUESTS);

        // The change requests are read at once: each one is a separate call and reading them in turn
        // would show a spinner for as long as the slowest chain, not the slowest call.
        const found = await Promise.all(applied.map(changeRequest => toPortalEntry(target, changeRequest, signal)));
        if (signal.aborted) return;

        const changes = found
          .filter((entry): entry is ChangeLogEntry => !!entry)
          .sort((a, b) => (b.changedAt ?? '').localeCompare(a.changedAt ?? ''))
          .slice(0, MAX_ENTRIES);

        if (!signal.aborted) setEntries(changes);
      } catch {
        if (!signal.aborted) setEntries([]);
      } finally {
        if (!signal.aborted) setIsFetching(false);
      }
    },
    [target],
  );

  useEffect(() => {
    if (!target) {
      setEntries([]);
      return;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [target, load]);

  return { entries, isFetching };
};
